/* eslint-disable react/no-array-index-key */
import PropTypes from 'prop-types';
import React, {
  useCallback,
  useEffect, useMemo, useRef, useState,
} from 'react';
import { v4 as uuidv4 } from 'uuid';

import { Icon } from '@powdr/components';
import { deslugify, getImageData } from '@powdr/utils';

import {
  MarqueeImage, MarqueeItem, StyledImageMarquee, MarqueeTrack,
  MarqueeSet,
  MarqueeItemWrapper,
} from './styles';

export const ImageMarquee = ({
  parentColorProfile = null,
  className = '',
  relationships,
}) => {
  const containerRef = useRef(null);
  const singleSetRef = useRef(null);
  const baseItems = useMemo(() => relationships?.marqueeItems || [], [relationships]);
  const [repeatCount, setRepeatCount] = useState(1);
  const itemsWithIds = useMemo(
    () => baseItems.map((item) => ({ ...item, id: uuidv4() })),
    [baseItems],
  );

  useEffect(() => {
    if (!containerRef.current || !singleSetRef.current || baseItems.length === 0) return;

    const containerWidth = containerRef.current.offsetWidth;
    const itemSetWidth = singleSetRef.current.offsetWidth;

    if (itemSetWidth === 0) return;

    const neededRepeatCount = Math.max(
      2,
      Math.ceil((containerWidth * 2) / itemSetWidth),
    );

    setRepeatCount(neededRepeatCount);
  }, [baseItems]);

  const RenderMarqueeSet = useCallback(({ forwardedRef }) => (
    <MarqueeSet ref={forwardedRef}>
      {itemsWithIds.map((item) => {
        const marqueeImageData = getImageData(item?.relationships?.marqueeImage || null);
        const button = item?.relationships?.button;

        return (
          <MarqueeItemWrapper
            key={item.id}
            href={button?.buttonLink}
            target={button?.buttonTarget}
            title={deslugify(item?.iconName || '')}
          >
            <MarqueeItem $colorProfile={parentColorProfile}>
              {item.iconName && !marqueeImageData?.src && (
                <Icon
                  className="marquee-icon"
                  name={item.iconName}
                  width="150"
                />
              )}
              {marqueeImageData?.src && (
                <MarqueeImage
                  objectFit="contain"
                  image={marqueeImageData.src}
                  alt={marqueeImageData.altText || ''}
                />
              )}
            </MarqueeItem>
          </MarqueeItemWrapper>
        );
      })}
    </MarqueeSet>
  ), [parentColorProfile, itemsWithIds]);

  return (
    <StyledImageMarquee className={className} ref={containerRef}>
      <MarqueeTrack $repeatCount={repeatCount}>
        <RenderMarqueeSet forwardedRef={singleSetRef} />
        {[...Array(repeatCount - 1)].map((_, idx) => (
          <RenderMarqueeSet key={`repeat-marquee-set${idx}`} />
        ))}
      </MarqueeTrack>
    </StyledImageMarquee>
  );
};

ImageMarquee.propTypes = {
  className: PropTypes.string,
  parentColorProfile: PropTypes.string,
  relationships: PropTypes.shape({
    marqueeItems: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
};
